import React from 'react';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import { contactDisplayName } from '../../utils/contactName';
import { formatPhoneInput } from '../../utils/phone';
import './projectContactsPicker.css';

// Attach several contacts to a job and choose which one is primary.
//
// PRIMARY IS Project.CustomerId. That is deliberate: every automation, email and
// text already resolves the person to contact through Project.Customer, so making
// primary mean anything else would mean auditing all of them. Promoting a contact
// swaps it into customerId and pushes the previous primary down into the extra
// list — one record moves, nothing downstream changes.
//
// The search stays usable after a pick rather than collapsing into the chosen
// name, because attaching a second contact is the normal case on property
// management work, not an edge case.
function ProjectContactsPicker({
  options,          // [{ id, displayName, ... }] — searchable contacts
  primaryId,        // formData.customerId
  extras,           // [{ customerId, name, role }] — additional, not yet primary
  onChangePrimary,  // (customerId) => void
  onChangeExtras,   // (nextExtras) => void
  onCreateContact,  // async ({name, phone, email}) => createdContact — omit to hide
  disabled = false,
}) {
  // The inline "contact does not exist yet" form. Null when closed.
  const [draft, setDraft] = React.useState(null);
  const [savingDraft, setSavingDraft] = React.useState(false);
  const [draftError, setDraftError] = React.useState('');

  const saveDraft = async () => {
    const name = (draft?.name || '').trim();
    if (!name) {
      setDraftError('A name is required.');
      return;
    }

    // Match an existing contact on the phone's last ten digits before creating
    // anything. People type names inconsistently — "Bob Smith", "Robert Smith",
    // "Smith, Bob" — so a name check misses duplicates that a number catches. Same
    // rule the tenant auto-create and the messages inbox already use.
    const digits = (draft.phone || '').replace(/\D/g, '').slice(-10);
    if (digits.length === 10) {
      const existing = options.find((o) =>
        [o.phone, o.mobilePhone, o.mainPhone, o.workPhone]
          .some((p) => p && String(p).replace(/\D/g, '').slice(-10) === digits));
      if (existing) {
        // Quietly use the contact already on file rather than making a second row.
        handlePick({ target: { value: String(existing.id) } });
        setDraft(null);
        setDraftError('');
        return;
      }
    }

    setSavingDraft(true);
    setDraftError('');
    try {
      const created = await onCreateContact({
        name,
        phone: draft.phone || '',
        email: (draft.email || '').trim(),
      });
      if (created?.id) handlePick({ target: { value: String(created.id) } });
      setDraft(null);
    } catch (err) {
      const data = err.response?.data;
      setDraftError(typeof data === 'string' ? data : data?.message || 'Could not create the contact.');
    } finally {
      setSavingDraft(false);
    }
  };
  const byId = React.useMemo(() => {
    const m = new Map();
    options.forEach((o) => m.set(String(o.id), o));
    return m;
  }, [options]);

  const primary = primaryId ? byId.get(String(primaryId)) : null;

  const attachedIds = new Set([
    ...(primaryId ? [String(primaryId)] : []),
    ...extras.map((e) => String(e.customerId)),
  ]);

  // Someone already on the job should not be offered again — picking them twice
  // is never what anyone means, and it would produce a duplicate row.
  const selectable = options.filter((o) => !attachedIds.has(String(o.id)));

  const handlePick = (e) => {
    const id = e?.target?.value;
    if (!id) return;

    // The first contact chosen becomes primary; after that they stack up as extras
    // so the common single-contact job needs no extra clicks.
    if (!primaryId) onChangePrimary(id);
    else if (!attachedIds.has(String(id))) {
      onChangeExtras([...extras, { customerId: id, role: '' }]);
    }
  };

  const makePrimary = (customerId) => {
    const demoted = primaryId ? [{ customerId: String(primaryId), role: '' }] : [];
    onChangeExtras([
      ...extras.filter((x) => String(x.customerId) !== String(customerId)),
      ...demoted,
    ]);
    onChangePrimary(customerId);
  };

  const removeExtra = (customerId) =>
    onChangeExtras(extras.filter((x) => String(x.customerId) !== String(customerId)));

  const clearPrimary = () => {
    // Promote the first extra rather than leaving a job with contacts but no
    // primary — the API requires a customer, and a silent invalid state here
    // would only surface as a save failure.
    const [next, ...rest] = extras;
    if (next) {
      onChangePrimary(next.customerId);
      onChangeExtras(rest);
    } else {
      onChangePrimary('');
    }
  };

  const nameFor = (id) => {
    const row = byId.get(String(id));
    return row ? (row.displayName || contactDisplayName(row)) : `Contact #${id}`;
  };

  // Company, email and phone under the name. A PM job attaches several people from
  // the same agency, and the name alone does not say which one you picked.
  const detailFor = (id) => {
    const row = byId.get(String(id));
    if (!row) return null;
    const bits = [row.companyName, row.phone, row.email, row.address].filter(Boolean);
    return bits.length ? bits.join(' · ') : 'no contact details on file';
  };

  return (
    <div className="pcp">
      <SearchableSelect
        options={selectable}
        value=""
        onChange={handlePick}
        placeholder={primaryId ? 'Add another contact…' : 'Search for a contact…'}
        displayKey="displayName"
        valueKey="id"
        name="__contactPicker"
        detailKeys={["companyName", "phone", "email", "address"]}
        onCreateNew={onCreateContact ? (term) => setDraft({
          // Whatever was typed becomes the name, since that is what people type
          // first. If it was a phone number instead, the phone field catches it.
          name: /\d{3}/.test(term) ? '' : term,
          phone: /\d{3}/.test(term) ? term : '',
          email: '',
        }) : null}
        createLabel="Add new contact"
      />

      {/* Name, phone and email — enough for the contact to be usable immediately.
          A name on its own cannot be texted or emailed, which is most of what a
          contact is for on a job, so it would just need editing later anyway. */}
      {draft && (
        <div className="pcp-draft">
          <div className="pcp-draft-head">
            <span>New contact</span>
            <button type="button" onClick={() => { setDraft(null); setDraftError(''); }}>Cancel</button>
          </div>

          {draftError && <div className="pcp-draft-error">{draftError}</div>}

          <div className="pcp-draft-fields">
            <input
              type="text"
              className="form-input"
              placeholder="Name *"
              value={draft.name}
              onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
              maxLength={200}
              autoFocus
            />
            <input
              type="tel"
              className="form-input"
              placeholder="Phone"
              value={draft.phone}
              onChange={(e) => setDraft((p) => ({ ...p, phone: formatPhoneInput(e.target.value) }))}
            />
            <input
              type="email"
              className="form-input"
              placeholder="Email"
              value={draft.email}
              onChange={(e) => setDraft((p) => ({ ...p, email: e.target.value }))}
              maxLength={100}
            />
            <button type="button" className="btn-primary" onClick={saveDraft} disabled={savingDraft}>
              {savingDraft ? 'Saving…' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {(primary || extras.length > 0) && (
        <ul className="pcp-list">
          {primary && (
            <li className="pcp-row pcp-primary">
              <span className="pcp-badge">Primary</span>
              <span className="pcp-who">
                <span className="pcp-name">{nameFor(primaryId)}</span>
                <span className="pcp-detail">{detailFor(primaryId)}</span>
              </span>
              <button
                type="button"
                className="pcp-action"
                onClick={clearPrimary}
                disabled={disabled}
                title="Remove from this job"
              >
                ✕
              </button>
            </li>
          )}

          {extras.map((x) => (
            <li className="pcp-row" key={x.customerId}>
              <button
                type="button"
                className="pcp-make-primary"
                onClick={() => makePrimary(x.customerId)}
                disabled={disabled}
                title="Make this the primary contact"
              >
                Make primary
              </button>
              <span className="pcp-who">
                <span className="pcp-name">{nameFor(x.customerId)}</span>
                <span className="pcp-detail">{detailFor(x.customerId)}</span>
              </span>
              <button
                type="button"
                className="pcp-action"
                onClick={() => removeExtra(x.customerId)}
                disabled={disabled}
                title="Remove from this job"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="pcp-hint">
        The primary contact is who the job is for — automations, emails and texts
        use them. Others are attached for reference and notifications.
      </div>
    </div>
  );
}

export default ProjectContactsPicker;
